import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import NavbarSmallWebsite from "../components/NavbarSmallWebsite";
import { getCsrfCookie } from "../lib/utils";

export const Route = createFileRoute("/secretsignup")({
  component: SecretSignup,
});

type SignupStatus = "idle" | "loading" | "success" | "error";

function SecretSignup() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<SignupStatus>("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    void fetch("/accounts/whoami", { credentials: "include" });
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("");
    try {
      const res = await fetch("/api/secret-signup/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail =
          data?.details?.join?.(" ") || data?.error || "Signup failed.";
        throw new Error(detail);
      }
      setStatus("success");
      setMessage(
        "Account created. We’ll activate your access within 24 hours.",
      );
      setUsername("");
      setPassword("");
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error ? error.message : "Signup failed.",
      );
    }
  };

  return (
    <div className="pt-20 md:min-h-screen w-full flex items-center justify-center bg-slate-50 px-4 font-TikTok">
      <NavbarSmallWebsite />
      <section className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 shadow-md w-full md:max-w-md">
        <h2 className="text-xl md:text-2xl font-semibold text-left">
          Secret signup
        </h2>
        <p className="text-sm font-light mb-8 text-left text-slate-600">
          Create your account. We’ll activate it within 24 hours.
        </p>

        {status === "success" ? (
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 p-6 text-left shadow-sm">
            <div className="inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
              Ready for launch
            </div>
            <h3 className="mt-4 text-2xl font-semibold text-slate-900">
              Account created
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              We’ll activate your access within 24 hours.
            </p>
            <div className="mt-6 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                We’ll notify you as soon as it’s live.
              </span>
              <Link
                to="/login"
                className="text-sm font-semibold text-blue-600 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form className="space-y-4 text-left" onSubmit={handleSubmit}>
            <div>
              <label className="block mb-1 text-sm font-medium">Username</label>
              <input
                type="text"
                placeholder="Choose a username"
                className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">Password</label>
              <input
                type="password"
                placeholder="Create a password"
                className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="new-password"
              />
            </div>

            {message && (
              <div className="text-sm text-rose-600">{message}</div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-slate-200"
              disabled={status === "loading"}
            >
              {status === "loading" ? "Creating account..." : "Create account"}
            </button>

            <div className="pt-4 flex justify-between items-center text-sm text-neutral-600">
              <span>Already have access?</span>
              <Link to="/login" className="text-blue-600 hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
